import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Spin, Alert } from 'antd';

import AdminHeader from '../../components/AdminHeader';
import ImageUploader from '../../components/ImageUploader';
import api, { getApiErrorMessage } from '../../api/api';
import { UserProfile } from '../../models';

const { TextArea } = Input;

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/v1/profile/');
        const data = response.data as UserProfile;
        setProfile(data);
        setName(data.name || '');
        setBio(data.bio || '');
      } catch (err) {
        console.error('Failed to load profile', err);
        setProfileMessage({
          type: 'error',
          text: getApiErrorMessage(err, 'Failed to load profile.'),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleImageChange = (_file: File | null) => {
    // ImageUploader uploads immediately; refresh preview URL from server
    api.get('/v1/profile/').then((response) => {
      const data = response.data as UserProfile;
      setProfile(data);
    });
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setSaving(true);
    setProfileMessage(null);

    try {
      const response = await api.patch('/v1/profile/update/', {
        name,
        bio,
      });
      const data = response.data as UserProfile;
      setProfile(data);
      setName(data.name || '');
      setBio(data.bio || '');
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setProfileMessage({
        type: 'error',
        text: getApiErrorMessage(err, 'Failed to update profile. Please try again.'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminHeader>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">
            Profile
          </h1>

          {loading ? (
            <div className="flex justify-center">
              <Spin size="large" />
            </div>
          ) : (
            <div className="space-y-8">
              <form
                onSubmit={handleSaveProfile}
                className="bg-white shadow rounded-lg p-6"
              >
                {profileMessage && (
                  <Alert
                    message={profileMessage.text}
                    type={profileMessage.type}
                    showIcon
                    className="mb-6"
                  />
                )}

                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Profile Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Update your profile picture and display details.
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Profile Picture
                    </label>
                    <ImageUploader
                      initialImage={profile?.profile_picture}
                      onImageChange={handleImageChange}
                    />
                  </div>

                  <div className="sm:col-span-4">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email
                    </label>
                    <Input
                      id="email"
                      value={profile?.user?.email || ''}
                      disabled
                    />
                  </div>

                  <div className="sm:col-span-4">
                    <label
                      htmlFor="username"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Username
                    </label>
                    <Input
                      id="username"
                      value={profile?.username || ''}
                      disabled
                      prefix="@"
                    />
                  </div>

                  <div className="sm:col-span-4">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Display Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="sm:col-span-6">
                    <label
                      htmlFor="bio"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Bio
                    </label>
                    <TextArea
                      id="bio"
                      name="bio"
                      rows={3}
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Brief description for your profile."
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {saving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </div>
              </form>

              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Plan
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  View and manage your subscription plan.
                </p>
                <div className="mt-4">
                  <Link
                    to="/admin/subscription"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Manage Plan →
                  </Link>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Security
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Manage your account password.
                </p>
                <div className="mt-4">
                  <Link
                    to="/admin/change-password"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Change Password →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminHeader>
  );
};

export default Profile;
